import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { inventoryApi } from '../../api/inventoryApi';
import { formatQuantity, formatQuantityParts } from '../../utils/formatQuantity';
import type { ThemeColors } from '../../context/ThemeContext';

/** One ingredient's movements on a single day (quantities in that ingredient's OWN unit). */
interface DashboardRow {
  inventoryItemId: number;
  name: string;
  unit: string;
  date: string; // yyyy-MM-dd
  used: number;
  stockIn: number;
  wasted: number;
}

interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

interface InventoryOverviewTabProps {
  theme: ThemeColors;
}

type Preset = '7d' | '30d' | 'month' | 'custom';

const pad = (n: number) => String(n).padStart(2, '0');
const toISODate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Compute [from, to] (inclusive) for a named preset, anchored on today. */
const presetRange = (preset: Exclude<Preset, 'custom'>): { from: string; to: string } => {
  const today = new Date();
  const to = toISODate(today);
  if (preset === 'month') {
    return { from: toISODate(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  }
  const fromDate = new Date(today);
  fromDate.setDate(today.getDate() - (preset === '7d' ? 6 : 29));
  return { from: toISODate(fromDate), to };
};

/** Every calendar date in [from, to] inclusive, so the chart axis has no gaps. */
const datesInRange = (from: string, to: string): string[] => {
  if (!from || !to || from > to) return [];
  const out: string[] = [];
  const end = new Date(`${to}T00:00:00`);
  const cursor = new Date(`${from}T00:00:00`);
  // guard against pathological ranges
  for (let i = 0; cursor <= end && i < 400; i += 1) {
    out.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const dayLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/** Display unit label scaled to a representative magnitude, e.g. GRAMS + 9400 → "kg". */
const displayUnit = (unit: string, magnitude: number) =>
  formatQuantityParts(magnitude > 0 ? magnitude : 1, unit).label;

// --- Unit families: only units in the same family may share a comparison chart ---
const UNIT_FAMILY: Record<string, string> = {
  GRAMS: 'weight',
  KG: 'weight',
  ML: 'volume',
  LITRE: 'volume',
  PIECES: 'pieces',
  UNITS: 'units',
};

/** Family key for an ingredient unit; unknown units are their own family. */
const unitFamily = (unit: string): string =>
  UNIT_FAMILY[(unit || '').toUpperCase()] || (unit || '').toUpperCase();

/** Canonical display unit + label per family, so a mixed chart is normalised honestly. */
const CANONICAL: Record<string, { unit: string; label: string }> = {
  weight: { unit: 'KG', label: 'kg' },
  volume: { unit: 'LITRE', label: 'L' },
  pieces: { unit: 'PIECES', label: 'pcs' },
  units: { unit: 'UNITS', label: 'units' },
};

/** Multiplier to convert a value in `unit` into its family's canonical unit. */
const toCanonicalFactor = (unit: string): number => {
  const u = (unit || '').toUpperCase();
  if (u === 'GRAMS' || u === 'ML') return 0.001; // g→kg, ml→L
  return 1;
};

/** Canonical unit + label for a given ingredient unit. */
const canonicalInfo = (unit: string): { unit: string; label: string } => {
  const c = CANONICAL[unitFamily(unit)];
  return c || { unit, label: (unit || '').toLowerCase() };
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Max items in the comparison chart, for readability. */
const MAX_COMPARE = 5;

interface IngredientAgg {
  inventoryItemId: number;
  name: string;
  unit: string;
  used: number;
  stockIn: number;
  wasted: number;
  movementRows: number;
  unitLabel: string;
}

/**
 * Inventory Overview dashboard: answers "how much was used / bought / wasted, on what,
 * and when" over a date range. Quantities stay in each ingredient's own unit and are
 * never summed across ingredients.
 */
const InventoryOverviewTab: React.FC<InventoryOverviewTabProps> = ({ theme }) => {
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  const initial = presetRange('7d');
  const [preset, setPreset] = useState<Preset>('7d');
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);

  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardBorder = theme.components?.card?.border;
  const cardBg = theme.components?.card?.background;

  // Per-ingredient aggregate over the whole period, sorted by movement rows.
  const ingredients = useMemo<IngredientAgg[]>(() => {
    const map = new Map<number, IngredientAgg>();
    rows.forEach((r) => {
      const prev = map.get(r.inventoryItemId);
      if (prev) {
        prev.used += r.used || 0;
        prev.stockIn += r.stockIn || 0;
        prev.wasted += r.wasted || 0;
        prev.movementRows += 1;
      } else {
        map.set(r.inventoryItemId, {
          inventoryItemId: r.inventoryItemId,
          name: r.name,
          unit: r.unit,
          used: r.used || 0,
          stockIn: r.stockIn || 0,
          wasted: r.wasted || 0,
          movementRows: 1,
          unitLabel: '',
        });
      }
    });
    const list = Array.from(map.values()).map((a) => ({
      ...a,
      unitLabel: displayUnit(a.unit, Math.max(a.used, a.stockIn, a.wasted)),
    }));
    list.sort((a, b) => b.movementRows - a.movementRows || b.used - a.used);
    return list;
  }, [rows]);

  const fetchDashboard = useCallback(async (rangeFrom: string, rangeTo: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await inventoryApi.getDashboard(rangeFrom, rangeTo);
      const nextRows: DashboardRow[] = res.data?.rows || [];
      setRows(nextRows);
      // Default the chart to the ingredient with the most movement rows.
      const counts = new Map<number, number>();
      nextRows.forEach((r) => counts.set(r.inventoryItemId, (counts.get(r.inventoryItemId) || 0) + 1));
      let bestId: number | null = null;
      let best = -1;
      counts.forEach((count, id) => {
        if (count > best) {
          best = count;
          bestId = id;
        }
      });
      setSelectedId(bestId);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to load overview');
      setRows([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Any change to the range (preset chip or manual date) refetches.
  useEffect(() => {
    fetchDashboard(from, to);
  }, [from, to, fetchDashboard]);

  const applyPreset = (next: Exclude<Preset, 'custom'>) => {
    const range = presetRange(next);
    setPreset(next);
    setFrom(range.from);
    setTo(range.to);
  };

  const onFromChange = (value: string) => {
    setPreset('custom');
    setFrom(value);
  };
  const onToChange = (value: string) => {
    setPreset('custom');
    setTo(value);
  };

  const selected = ingredients.find((i) => i.inventoryItemId === selectedId) || null;

  // Daily series for the selected ingredient, gap-filled with zeros.
  const chartData = useMemo(() => {
    const dates = datesInRange(from, to);
    if (!selected) return [];
    const byDate = new Map<string, DashboardRow>();
    rows.forEach((r) => {
      if (r.inventoryItemId === selected.inventoryItemId) byDate.set(r.date, r);
    });
    return dates.map((date) => {
      const r = byDate.get(date);
      return {
        date,
        label: dayLabel(date),
        used: r?.used || 0,
        stockIn: r?.stockIn || 0,
        wasted: r?.wasted || 0,
      };
    });
  }, [rows, selected, from, to]);

  // Summary counts (counts of things, so mixing units is fine here).
  const summary = useMemo(() => {
    const activeIngredients = new Set(rows.map((r) => r.inventoryItemId)).size;
    const daysWithUsage = new Set(rows.filter((r) => (r.used || 0) > 0).map((r) => r.date)).size;
    const wastageEvents = rows.filter((r) => (r.wasted || 0) > 0).length;
    return { activeIngredients, daysWithUsage, wastageEvents };
  }, [rows]);

  // On new data, default the comparison to the top-3 used items sharing the busiest
  // item's unit family (mixing incompatible units in one chart is never allowed).
  useEffect(() => {
    if (ingredients.length === 0) {
      setCompareIds([]);
      return;
    }
    const byUsed = [...ingredients].sort((a, b) => b.used - a.used);
    const family = unitFamily(byUsed[0].unit);
    setCompareIds(
      byUsed
        .filter((i) => unitFamily(i.unit) === family)
        .slice(0, 3)
        .map((i) => i.inventoryItemId),
    );
  }, [ingredients]);

  // Family currently locked in by the comparison selection (null when empty).
  const compareFamily = compareIds.length
    ? unitFamily(ingredients.find((i) => i.inventoryItemId === compareIds[0])?.unit || '')
    : null;
  const compareUnit = compareIds.length
    ? canonicalInfo(ingredients.find((i) => i.inventoryItemId === compareIds[0])?.unit || '')
    : null;

  const toggleCompare = (id: number) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_COMPARE) return prev;
      const itemFamily = unitFamily(ingredients.find((i) => i.inventoryItemId === id)?.unit || '');
      if (compareFamily && itemFamily !== compareFamily) return prev; // never mix families
      return [...prev, id];
    });
  };

  // Grouped daily USED series, one key per selected item, normalised to the canonical unit.
  const compareChartData = useMemo(() => {
    const dates = datesInRange(from, to);
    if (compareIds.length === 0) return [];
    const byItemDate = new Map<string, number>();
    rows.forEach((r) => {
      if (!compareIds.includes(r.inventoryItemId)) return;
      const key = `${r.inventoryItemId}|${r.date}`;
      const val = (r.used || 0) * toCanonicalFactor(r.unit);
      byItemDate.set(key, (byItemDate.get(key) || 0) + val);
    });
    return dates.map((date) => {
      const point: Record<string, number | string> = { date, label: dayLabel(date) };
      compareIds.forEach((id) => {
        point[`item_${id}`] = round2(byItemDate.get(`${id}|${date}`) || 0);
      });
      return point;
    });
  }, [rows, compareIds, from, to]);

  const topMovers = ingredients.slice(0, 10);
  // Rank by canonical-normalised wastage so g vs kg (and ml vs L) sort honestly.
  const topWasted = ingredients
    .filter((i) => i.wasted > 0)
    .sort((a, b) => b.wasted * toCanonicalFactor(b.unit) - a.wasted * toCanonicalFactor(a.unit))
    .slice(0, 10);

  // Distinct colours for the comparison series (warm Scandinavian palette).
  const comparePalette = [
    theme.primary,
    theme.success || '#4CAF50',
    theme.accent || '#8a6d5b',
    theme.danger || '#D99982',
    theme.gray?.[500] || '#6b8fb5',
  ];

  const usedColor = theme.primary;
  const stockInColor = theme.success || '#4CAF50';
  const wastageColor = theme.danger || '#D99982';

  const presetChips: { key: Exclude<Preset, 'custom'>; label: string; testId: string }[] = [
    { key: '7d', label: 'Last 7 days', testId: 'overview-range-7d' },
    { key: '30d', label: 'Last 30 days', testId: 'overview-range-30d' },
    { key: 'month', label: 'This month', testId: 'overview-range-month' },
  ];

  const summaryCards: { testId: string; label: string; value: number; accent: string }[] = [
    { testId: 'overview-summary-active', label: 'Active ingredients', value: summary.activeIngredients, accent: theme.primary },
    { testId: 'overview-summary-days', label: 'Days with usage', value: summary.daysWithUsage, accent: stockInColor },
    { testId: 'overview-summary-wastage', label: 'Wastage events', value: summary.wastageEvents, accent: wastageColor },
  ];

  const dateInputSx = {
    '& .MuiInputBase-input': { color: theme.text, fontSize: '0.8rem', py: 0.75 },
    '& .MuiInputLabel-root': { color: theme.gray?.[500], fontSize: '0.75rem' },
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header: presets + custom range */}
      <Card sx={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {presetChips.map((chip) => {
                const active = preset === chip.key;
                return (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onClick={() => applyPreset(chip.key)}
                    data-testid={chip.testId}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      height: 30,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: active ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                      color: active ? theme.primary : theme.gray?.[600] || '#666',
                      border: active ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                      '&:hover': { backgroundColor: active ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
                    }}
                  />
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: { sm: 'auto' } }}>
              <TextField
                label="From"
                type="date"
                size="small"
                value={from}
                onChange={(e) => onFromChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'overview-from', max: to }}
                sx={{ ...dateInputSx, minWidth: 140 }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={to}
                onChange={(e) => onToChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'overview-to', min: from }}
                sx={{ ...dateInputSx, minWidth: 140 }}
              />
              {loading && <CircularProgress size={18} sx={{ color: theme.primary }} />}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {rows.length === 0 ? (
        <Card sx={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="body2" sx={{ color: theme.gray?.[600], textAlign: 'center' }}>
              No stock movements in this period.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary strip */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1.5,
              mb: 3,
            }}
          >
            {summaryCards.map((card) => (
              <Box
                key={card.testId}
                data-testid={card.testId}
                sx={{
                  backgroundColor: cardBg || '#FFFFFF',
                  border: `1px solid ${cardBorder}`,
                  borderLeft: `3px solid ${card.accent}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.5,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.text, lineHeight: 1.1 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                  {card.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Usage chart */}
          <Card sx={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
                    Daily movement
                  </Typography>
                  {selected && (
                    <Typography variant="body2" data-testid="overview-chart-title" sx={{ color: theme.gray?.[600] }}>
                      {selected.name} ({selected.unitLabel})
                    </Typography>
                  )}
                </Box>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={selected ? String(selected.inventoryItemId) : ''}
                    onChange={(e) => setSelectedId(Number(e.target.value))}
                    displayEmpty
                    inputProps={{ 'data-testid': 'overview-item-select' }}
                    sx={{
                      color: theme.text,
                      fontSize: '0.85rem',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: cardBorder },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
                    }}
                  >
                    {ingredients.map((ing) => (
                      <MenuItem key={ing.inventoryItemId} value={String(ing.inventoryItemId)}>
                        {ing.name} ({ing.unitLabel})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box data-testid="overview-chart" sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.gray?.[200] || '#E5E5E5'} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: theme.gray?.[500] || '#666' }}
                      tickLine={false}
                      axisLine={{ stroke: theme.gray?.[300] || '#CCC' }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: theme.gray?.[500] || '#666' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatQuantity(value, selected?.unit || ''),
                        name,
                      ]}
                      contentStyle={{
                        backgroundColor: cardBg || '#FFF',
                        border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                        borderRadius: 8,
                      }}
                      cursor={{ fill: `${theme.primary}11` }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="used" name="Used" fill={usedColor} />
                    <Bar dataKey="stockIn" name="Stock in" fill={stockInColor} />
                    <Bar dataKey="wasted" name="Wastage" fill={wastageColor} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Compare items — grouped daily USED across same-unit-family items */}
          <Card sx={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text }}>
                  Compare items
                </Typography>
                {compareUnit && (
                  <Typography variant="caption" data-testid="overview-compare-hint" sx={{ color: theme.gray?.[600] }}>
                    Comparing daily usage in {compareUnit.label}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {ingredients.map((ing) => {
                  const on = compareIds.includes(ing.inventoryItemId);
                  const differentFamily = !!compareFamily && unitFamily(ing.unit) !== compareFamily;
                  const capped = !on && compareIds.length >= MAX_COMPARE;
                  const disabled = differentFamily || capped;
                  return (
                    <Chip
                      key={ing.inventoryItemId}
                      label={`${ing.name} (${ing.unitLabel})`}
                      onClick={() => !disabled && toggleCompare(ing.inventoryItemId)}
                      disabled={disabled}
                      data-testid={`overview-compare-item-${ing.inventoryItemId}`}
                      data-selected={on ? 'true' : 'false'}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        height: 30,
                        borderRadius: '8px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        backgroundColor: on ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                        color: on ? theme.primary : theme.gray?.[600] || '#666',
                        border: on ? `1.5px solid ${theme.primary}` : '1px solid transparent',
                        '&:hover': { backgroundColor: on ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
                        '&.Mui-disabled': { opacity: 0.4 },
                      }}
                    />
                  );
                })}
              </Box>

              {compareIds.length === 0 ? (
                <Typography variant="body2" sx={{ color: theme.gray?.[600], py: 3, textAlign: 'center' }}>
                  Select items above to compare their daily usage.
                </Typography>
              ) : (
                <Box data-testid="overview-compare-chart" sx={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={compareChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.gray?.[200] || '#E5E5E5'} vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: theme.gray?.[500] || '#666' }}
                        tickLine={false}
                        axisLine={{ stroke: theme.gray?.[300] || '#CCC' }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: theme.gray?.[500] || '#666' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          formatQuantity(value, compareUnit?.unit || ''),
                          name,
                        ]}
                        contentStyle={{
                          backgroundColor: cardBg || '#FFF',
                          border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                          borderRadius: 8,
                        }}
                        cursor={{ fill: `${theme.primary}11` }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {compareIds.map((id, idx) => {
                        const ing = ingredients.find((i) => i.inventoryItemId === id);
                        return (
                          <Bar
                            key={id}
                            dataKey={`item_${id}`}
                            name={ing?.name || `#${id}`}
                            fill={comparePalette[idx % comparePalette.length]}
                          />
                        );
                      })}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Top movers */}
          <Card sx={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 2 }}>
                Top movers
              </Typography>

              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {topMovers.map((ing) => {
                    const active = ing.inventoryItemId === selectedId;
                    return (
                      <Box
                        key={ing.inventoryItemId}
                        data-testid="overview-top-row"
                        onClick={() => setSelectedId(ing.inventoryItemId)}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          cursor: 'pointer',
                          border: `1px solid ${active ? theme.primary : cardBorder}`,
                          backgroundColor: active ? `${theme.primary}0F` : theme.background,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
                          {ing.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                          <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                            Used {formatQuantity(ing.used, ing.unit)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                            Stock in {formatQuantity(ing.stockIn, ing.unit)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                            Wasted {formatQuantity(ing.wasted, ing.unit)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }}>Ingredient</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Used</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Stock in</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Wasted</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topMovers.map((ing) => {
                        const active = ing.inventoryItemId === selectedId;
                        return (
                          <TableRow
                            key={ing.inventoryItemId}
                            data-testid="overview-top-row"
                            onClick={() => setSelectedId(ing.inventoryItemId)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: active ? `${theme.primary}0F` : 'transparent',
                              '&:hover': { backgroundColor: `${theme.primary}08` },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{ing.name}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: theme.text }}>{formatQuantity(ing.used, ing.unit)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>{formatQuantity(ing.stockIn, ing.unit)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>{formatQuantity(ing.wasted, ing.unit)}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Top wasted */}
          <Card sx={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 2, mt: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.text, mb: 2 }}>
                Top wasted
              </Typography>

              {topWasted.length === 0 ? (
                <Typography
                  variant="body2"
                  data-testid="overview-wasted-empty"
                  sx={{ color: theme.gray?.[600], py: 2, textAlign: 'center' }}
                >
                  No wastage recorded in this period.
                </Typography>
              ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {topWasted.map((ing) => {
                    const active = ing.inventoryItemId === selectedId;
                    return (
                      <Box
                        key={ing.inventoryItemId}
                        data-testid="overview-wasted-row"
                        onClick={() => setSelectedId(ing.inventoryItemId)}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          border: `1px solid ${active ? theme.primary : cardBorder}`,
                          backgroundColor: active ? `${theme.primary}0F` : theme.background,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                          {ing.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: wastageColor }}>
                          {formatQuantity(ing.wasted, ing.unit)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }}>Ingredient</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: theme.text }} align="right">Wasted</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topWasted.map((ing) => {
                        const active = ing.inventoryItemId === selectedId;
                        return (
                          <TableRow
                            key={ing.inventoryItemId}
                            data-testid="overview-wasted-row"
                            onClick={() => setSelectedId(ing.inventoryItemId)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: active ? `${theme.primary}0F` : 'transparent',
                              '&:hover': { backgroundColor: `${theme.primary}08` },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: theme.text }}>{ing.name}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: wastageColor }}>
                                {formatQuantity(ing.wasted, ing.unit)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default InventoryOverviewTab;
